import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Select, message, Typography, Descriptions,
} from 'antd';
import { CopyOutlined, DeleteOutlined, KeyOutlined, UserAddOutlined } from '@ant-design/icons';
import { useTenant } from '../contexts/TenantContext';
import api from '../services/api';

const { Title } = Typography;

interface Member {
  id: string;
  user_id: string;
  email: string;
  username: string;
  role: string;
  joined_at: string;
}

interface PendingJoinRequest {
  id: string;
  user_id: string;
  email: string;
  real_name?: string;
  requested_role: string;
  created_at?: string;
}

interface TenantInvitation {
  id: string;
  email: string;
  requested_role: string;
  status: string;
  expires_at: string;
}

interface ApiCredentialItem {
  id: string;
  name: string;
  prefix: string;
  status: string;
  expires_at?: string;
  last_used_at?: string;
}

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理员' },
  { value: 'pharmacist', label: '药师' },
  { value: 'member', label: '成员' },
  { value: 'viewer', label: '只读' },
];

const ROLE_COLORS: Record<string, string> = {
  owner: 'red', admin: 'orange', pharmacist: 'blue', member: 'green', viewer: 'default',
};

const TenantSettings: React.FC = () => {
  const { currentTenant, canManageCurrentTenant } = useTenant();
  const [members, setMembers] = useState<Member[]>([]);
  const [joinRequests, setJoinRequests] = useState<PendingJoinRequest[]>([]);
  const [invitations, setInvitations] = useState<TenantInvitation[]>([]);
  const [memberLimit, setMemberLimit] = useState<number>(-1);
  const [apiCredentials, setApiCredentials] = useState<ApiCredentialItem[]>([]);
  const [apiKeyOpen, setApiKeyOpen] = useState(false);
  const [issuedApiKey, setIssuedApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();
  const [apiKeyForm] = Form.useForm();

  const fetchMembers = async () => {
    if (!currentTenant) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${currentTenant.id}/members`);
      setMembers(res.data || []);
      if (canManageCurrentTenant) {
        const pending = await api.get(`/api/tenants/${currentTenant.id}/join-requests`);
        setJoinRequests(pending.data || []);
        const invites = await api.get(`/api/tenants/${currentTenant.id}/invitations`);
        setInvitations(invites.data || []);
        const features = await api.get('/api/subscriptions/features');
        setMemberLimit(Number(features.data?.limits?.max_members ?? -1));
      } else {
        setJoinRequests([]);
      }
      if (currentTenant.role === 'owner') {
        try {
          const credentials = await api.get('/api/openclaw/credentials');
          setApiCredentials(credentials.data || []);
        } catch {
          setApiCredentials([]);
        }
      } else {
        setApiCredentials([]);
      }
    } catch { message.error('加载成员失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, [currentTenant, canManageCurrentTenant]);

  const decideJoinRequest = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      await api.post(`/api/tenants/${currentTenant!.id}/join-requests/${requestId}`, { action });
      message.success(action === 'approve' ? '加入申请已批准' : '加入申请已拒绝');
      fetchMembers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '处理加入申请失败');
    }
  };

  const createApiCredential = async (values: { name: string; expires_days: number }) => {
    try {
      const response = await api.post('/api/openclaw/credentials', values);
      setApiKeyOpen(false);
      apiKeyForm.resetFields();
      setIssuedApiKey(response.data.api_key);
      fetchMembers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '创建 API 凭证失败');
    }
  };

  const revokeApiCredential = async (credentialId: string) => {
    try {
      await api.delete(`/api/openclaw/credentials/${credentialId}`);
      message.success('API 凭证已撤销');
      fetchMembers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '撤销 API 凭证失败');
    }
  };

  const handleInvite = async (values: { email: string; role: string }) => {
    try {
      await api.post(`/api/tenants/${currentTenant!.id}/members`, values);
      message.success('邀请已发送');
      setInviteOpen(false);
      form.resetFields();
      fetchMembers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '邀请失败');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await api.post(`/api/tenants/${currentTenant!.id}/invitations/${invitationId}/resend`);
      message.success('邀请邮件已重新发送');
      fetchMembers();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '重发邀请失败');
    }
  };

  const handleRemove = async (memberId: string) => {
    Modal.confirm({
      title: '确认移除',
      content: '确定要移除该成员吗？',
      onOk: async () => {
        try {
          await api.delete(`/api/tenants/${currentTenant!.id}/members/${memberId}`);
          message.success('已移除');
          fetchMembers();
        } catch { message.error('移除失败'); }
      },
    });
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    try {
      await api.patch(`/api/tenants/${currentTenant!.id}/members/${memberId}`, { role });
      message.success('角色已更新');
      fetchMembers();
    } catch { message.error('更新失败'); }
  };

  const columns = [
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    { title: '用户名', dataIndex: 'username', key: 'username' },
    {
      title: '角色', dataIndex: 'role', key: 'role',
      render: (role: string, record: Member) => (
        role === 'owner' ? <Tag color="red">所有者</Tag> : (
          <Select size="small" value={role} onChange={v => handleRoleChange(record.id, v)}
            options={ROLE_OPTIONS} style={{ width: 100 }} />
        )
      ),
    },
    {
      title: '加入时间', dataIndex: 'joined_at', key: 'joined_at',
      render: (v: string) => v ? new Date(v).toLocaleDateString() : '-'
    },
    {
      title: '操作', key: 'action',
      render: (_: any, record: Member) => (
        record.role !== 'owner' ? (
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(record.id)}>
            移除
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={4}>机构设置</Title>

      <Card title="机构信息" style={{ marginBottom: 16 }}>
        <Descriptions column={2}>
          <Descriptions.Item label="成员席位">
            {members.length} / {memberLimit < 0 ? '不限' : memberLimit}
            {memberLimit >= 0 && invitations.filter((item) => item.status === 'pending').length > 0
              ? `（已预留 ${invitations.filter((item) => item.status === 'pending').length}）` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="机构名称">{currentTenant?.name}</Descriptions.Item>
          <Descriptions.Item label="标识">{currentTenant?.slug}</Descriptions.Item>
          <Descriptions.Item label="当前套餐">
            <Tag color="blue">{currentTenant?.plan || 'free'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={currentTenant?.status === 'active' ? 'green' : 'red'}>
              {currentTenant?.status}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="机构成员"
        extra={
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setInviteOpen(true)} disabled={!canManageCurrentTenant || (memberLimit >= 0 && members.length + invitations.filter((item) => item.status === 'pending').length >= memberLimit)}>
            邀请成员
          </Button>
        }
      >
        <Table columns={columns} dataSource={members} rowKey="id" loading={loading} pagination={false} />
      </Card>

      {canManageCurrentTenant && invitations.length > 0 && (
        <Card title="已发送邀请" style={{ marginTop: 16 }}>
          <Table rowKey="id" pagination={false} dataSource={invitations} columns={[
            { title: '邮箱', dataIndex: 'email' },
            { title: '角色', dataIndex: 'requested_role' },
            { title: '状态', dataIndex: 'status' },
            { title: '有效期', dataIndex: 'expires_at', render: (v: string) => new Date(v).toLocaleString() },
            { title: '操作', render: (_: unknown, record: TenantInvitation) => record.status === 'pending' ? (
              <Space size={4}>
                <Button size="small" onClick={() => handleResendInvitation(record.id)}>重发</Button>
                <Button danger size="small" onClick={async () => {
                  await api.delete(`/api/tenants/${currentTenant!.id}/invitations/${record.id}`);
                  fetchMembers();
                }}>撤销</Button>
              </Space>
            ) : null },
          ]} />
        </Card>
      )}

      {canManageCurrentTenant && joinRequests.length > 0 && (
        <Card title="待审批加入申请" style={{ marginTop: 16 }}>
          <Table
            rowKey="id"
            pagination={false}
            dataSource={joinRequests}
            columns={[
              { title: '邮箱', dataIndex: 'email' },
              { title: '姓名', dataIndex: 'real_name', render: (value?: string) => value || '-' },
              { title: '申请角色', dataIndex: 'requested_role' },
              {
                title: '操作',
                render: (_: unknown, record: PendingJoinRequest) => (
                  <Space>
                    <Button type="primary" size="small" onClick={() => decideJoinRequest(record.id, 'approve')}>批准</Button>
                    <Button danger size="small" onClick={() => decideJoinRequest(record.id, 'reject')}>拒绝</Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {currentTenant?.role === 'owner' && (
        <Card
          title="API 凭证"
          style={{ marginTop: 16 }}
          extra={<Button icon={<KeyOutlined />} onClick={() => setApiKeyOpen(true)}>新建</Button>}
        >
          <Table
            rowKey="id"
            pagination={false}
            dataSource={apiCredentials}
            columns={[
              { title: '名称', dataIndex: 'name' },
              { title: '前缀', dataIndex: 'prefix' },
              { title: '状态', dataIndex: 'status', render: (value: string) => <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag> },
              { title: '到期时间', dataIndex: 'expires_at', render: (value?: string) => value ? new Date(value).toLocaleDateString() : '-' },
              {
                title: '操作',
                render: (_: unknown, record: ApiCredentialItem) => record.status === 'active' ? (
                  <Button danger size="small" onClick={() => revokeApiCredential(record.id)}>撤销</Button>
                ) : null,
              },
            ]}
          />
        </Card>
      )}

      <Modal title="邀请成员" open={inviteOpen} onCancel={() => setInviteOpen(false)}
        onOk={() => form.submit()} okText="邀请">
        <Form form={form} onFinish={handleInvite} layout="vertical">
          <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="输入成员邮箱" />
          </Form.Item>
          <Form.Item name="role" label="角色" initialValue="member">
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新建 API 凭证"
        open={apiKeyOpen}
        onCancel={() => setApiKeyOpen(false)}
        onOk={() => apiKeyForm.submit()}
      >
        <Form form={apiKeyForm} layout="vertical" onFinish={createApiCredential}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="expires_days" label="有效天数" initialValue={365} rules={[{ required: true }]}>
            <InputNumber min={1} max={3650} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="API 凭证已生成"
        open={Boolean(issuedApiKey)}
        footer={<Button type="primary" onClick={() => setIssuedApiKey(null)}>完成</Button>}
        closable={false}
      >
        <Input.TextArea value={issuedApiKey || ''} readOnly autoSize={{ minRows: 2, maxRows: 4 }} />
        <Button
          icon={<CopyOutlined />}
          style={{ marginTop: 12 }}
          onClick={() => issuedApiKey && navigator.clipboard.writeText(issuedApiKey)}
        >
          复制
        </Button>
      </Modal>
    </div>
  );
};

export default TenantSettings;
